// Catalog is a flat key -> string map.
// Nested JSON catalogs flatten with dot-paths: { dashboard: { title: 'X' } } -> { 'dashboard.title': 'X' }.
export type Catalog = Readonly<Record<string, string>>;

export function flatten(input: unknown, prefix = ''): Catalog {
  const out: Record<string, string> = {};
  walk(input, prefix, out);
  return Object.freeze(out);
}

/** What a leaf actually is, for the refusal message (`null` and arrays are not "object"). */
function describe(node: unknown): string {
  if (node === null) return 'null';
  return Array.isArray(node) ? 'array' : typeof node;
}

function walk(node: unknown, prefix: string, out: Record<string, string>): void {
  if (typeof node === 'string') {
    out[prefix] = node;
    return;
  }
  if (node !== null && typeof node === 'object' && !Array.isArray(node)) {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      const next = prefix ? `${prefix}.${key}` : key;
      walk(value, next, out);
    }
    return;
  }
  // A non-string leaf used to be dropped here. The key then reached a user as its
  // own loud marker, and the en/es parity test could not catch it: a number or a
  // list added to BOTH locales vanishes from BOTH catalogs, leaving their key sets
  // equal. Refuse where the bad value enters — the locale file — instead.
  throw new TypeError(
    `[@developerz/i18n] catalog key "${prefix}" is a ${describe(node)}, not a string — ` +
      'every locale value must be a string (numbers and lists belong in the view, ' +
      'or as a {var} the caller interpolates)',
  );
}
